import React, { useEffect, useState } from "react";
import api, { API_BASE, extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { Download } from "lucide-react";
import { toast } from "sonner";

const AdminConsent = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  // Export endpoint is admin/staff only — hide the affordance for qa_manager
  // so the user never hits a 403 toast or the (browser-specific) "body stream
  // already read" runtime error from a fetch+blob() chain on a 403 response.
  const canExport = user?.role === "admin" || user?.role === "staff";

  useEffect(() => {
    api.get("/admin/consent-logs")
      .then((r) => setLogs(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(extractErrorMessage(e)));
  }, []);

  const download = async () => {
    if (!canExport) return;
    const token = localStorage.getItem("tsw_token");
    try {
      const res = await fetch(`${API_BASE}/admin/export/consent_logs.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        // Read the body ONCE as text and surface the structured detail.
        // Calling .blob() after .json()/.text() throws "body stream already
        // read" — guarding with res.ok prevents that.
        const text = await res.text();
        let detail = text;
        try { detail = JSON.parse(text)?.detail || text; } catch { /* ignore */ }
        throw new Error(`${res.status}: ${detail || t("apiFallback.exportFailed")}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "consent_logs.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.message || t("apiFallback.exportFailed"));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="tsw-overline">{t("admin.title")}</div>
          <h1 className="font-display font-bold text-3xl text-slate-900">{t("admin.consent")}</h1>
        </div>
        {canExport && (
          <button onClick={download} className="tsw-btn-secondary" data-testid="consent-export">
            <Download className="w-4 h-4" /> {t("admin.exportConsent")}
          </button>
        )}
      </div>
      <p className="text-sm text-slate-500">{t("admin.consentNote")}</p>

      <div className="tsw-card overflow-hidden">
        <table className="w-full text-sm" data-testid="consent-table">
          <thead className="bg-slate-50">
            <tr className="text-start text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">{t("consentLog.colWhen")}</th>
              <th className="px-4 py-3">{t("consentLog.colUser")}</th>
              <th className="px-4 py-3">{t("consentLog.colType")}</th>
              <th className="px-4 py-3">{t("consentLog.colGranted")}</th>
              <th className="px-4 py-3">{t("consentLog.colIp")}</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => {
              // Translate the consent_type display label while preserving
              // the internal database value for QA inspection via tooltip.
              const typeLbl = (() => {
                const r = t(`consentTypes.${l.consent_type}`);
                return r && r !== `consentTypes.${l.consent_type}` ? r : l.consent_type;
              })();
              return (
                <tr key={l.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 text-slate-700">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-slate-700 font-mono text-xs">{l.user_id}</td>
                  <td className="px-4 py-2.5"><span className="tsw-chip bg-slate-100 text-slate-700" title={l.consent_type}>{typeLbl}</span></td>
                  <td className="px-4 py-2.5">{l.granted ? <span className="tsw-chip bg-emerald-50 text-emerald-700">{t("consentLog.yes")}</span> : <span className="tsw-chip bg-slate-100 text-slate-600">{t("consentLog.no")}</span>}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{l.ip_address || "—"}</td>
                </tr>
              );
            })}
            {logs.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400" data-testid="consent-empty">{t("consentLog.empty")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminConsent;
